/**
 * MLMetricDrawerMount - Standalone Mount for ML Metric Drawer
 * ============================================================================
 * This component provides a way to mount the MLReadinessMetricDrawer
 * independently of the main React app. It's used when ML Readiness results
 * are rendered by vanilla JS (standalone mode) but we still want the
 * React drawer functionality.
 *
 * Usage from vanilla JS:
 * ```js
 * // Mount the drawer (call once when ML results are displayed)
 * window.mountMLMetricDrawer(jobId);
 *
 * // Later, dispatch events to open it
 * dispatchMLMetricClick('sufficiency', 75);
 * ```
 *
 * @version 1.0.0
 * @created 2026-01-29
 * ============================================================================
 */

import React, { useEffect, useCallback } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { MLReadinessMetricDrawer } from './MLReadinessMetricDrawer';
import { useMLMetricDrawer } from './useMLMetricDrawer';
import type { MLMetricClickEventDetail } from '../../types/mlReadinessMetric';
import { isValidMetricId } from '../../types/mlReadinessMetric';

// ============================================================================
// Inner Component
// ============================================================================

interface MLMetricDrawerInnerProps {
  jobId: string;
}

const MLMetricDrawerInner: React.FC<MLMetricDrawerInnerProps> = ({ jobId }) => {
  const {
    isOpen,
    metricDetail,
    loading,
    error,
    openDrawer,
    closeDrawer,
  } = useMLMetricDrawer(jobId);

  // Handle metric click events from vanilla JS
  const handleMetricClick = useCallback((event: CustomEvent<MLMetricClickEventDetail>) => {
    const { metricId } = event.detail;
    console.log('[MLMetricDrawerMount] Received ml-metric-click event:', metricId);
    if (isValidMetricId(metricId)) {
      openDrawer(metricId);
    } else {
      console.warn('[MLMetricDrawerMount] Invalid metric ID:', metricId);
    }
  }, [openDrawer]);

  // Listen for ml-metric-click events
  useEffect(() => {
    console.log('[MLMetricDrawerMount] Adding event listener for ml-metric-click');
    window.addEventListener('ml-metric-click', handleMetricClick as EventListener);
    return () => {
      console.log('[MLMetricDrawerMount] Removing event listener');
      window.removeEventListener('ml-metric-click', handleMetricClick as EventListener);
    };
  }, [handleMetricClick]);

  return (
    <MLReadinessMetricDrawer
      isOpen={isOpen}
      onClose={closeDrawer}
      metricDetail={metricDetail}
      loading={loading}
      error={error}
    />
  );
};

// ============================================================================
// Mount Function
// ============================================================================

let drawerRoot: Root | null = null;
let drawerContainer: HTMLDivElement | null = null;

/**
 * Mount the ML Metric Drawer as a standalone React component
 * Call this when ML Readiness results are displayed
 *
 * @param jobId - The ML Readiness job ID
 */
export function mountMLMetricDrawer(jobId: string): void {
  console.log('[MLMetricDrawerMount] Mounting drawer for job:', jobId);

  // Clean up any existing mount
  unmountMLMetricDrawer();

  // Create container element
  drawerContainer = document.createElement('div');
  drawerContainer.id = 'ml-metric-drawer-root';
  drawerContainer.setAttribute('data-job-id', jobId);
  document.body.appendChild(drawerContainer);

  // Create React root and render
  drawerRoot = createRoot(drawerContainer);
  drawerRoot.render(<MLMetricDrawerInner jobId={jobId} />);

  console.log('[MLMetricDrawerMount] Drawer mounted successfully');
}

/**
 * Unmount the ML Metric Drawer
 * Call this when leaving the ML Readiness page
 *
 * The actual `root.unmount()` is deferred to a microtask. This function is
 * called from a React effect cleanup (MLReadinessLegacyWrapper), which under
 * StrictMode (and on parent re-render/unmount) runs WHILE React is mid-render —
 * and synchronously unmounting a *separate* root during another root's render is
 * disallowed by React ("Attempted to synchronously unmount a root while React
 * was already rendering"). We null the module-level refs synchronously (so an
 * immediate remount creates fresh ones and never double-unmounts these), then
 * tear down the captured root/container after the current render completes.
 */
export function unmountMLMetricDrawer(): void {
  const root = drawerRoot;
  const container = drawerContainer;
  drawerRoot = null;
  drawerContainer = null;
  if (!root && !container) return;

  queueMicrotask(() => {
    if (root) {
      console.log('[MLMetricDrawerMount] Unmounting drawer');
      root.unmount();
    }
    if (container && container.parentNode) {
      container.parentNode.removeChild(container);
    }
  });
}

// ============================================================================
// Global Exports
// ============================================================================

// Extend Window interface for global functions
declare global {
  interface Window {
    mountMLMetricDrawer?: typeof mountMLMetricDrawer;
    unmountMLMetricDrawer?: typeof unmountMLMetricDrawer;
  }
}

// Make mount/unmount functions available globally for vanilla JS
if (typeof window !== 'undefined') {
  window.mountMLMetricDrawer = mountMLMetricDrawer;
  window.unmountMLMetricDrawer = unmountMLMetricDrawer;
}

export default MLMetricDrawerInner;
